import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { formatPeriodRange } from "./PeriodBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Separator } from "./ui/separator";
import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { BarChart3, CircleDot, CheckCircle2, RefreshCw } from "lucide-react";
import { cn } from "../lib/utils";

/* ISOLATED ADD-ON: Volume Tracker.
   READ-ONLY reporting layer. Reads existing Expense data through a read-only
   endpoint and displays weekly volumes. It NEVER writes or modifies any
   financial data, balances, payments, or accounting periods.

   Formulas (server-side, mandatory sign conversion):
     Brand Volume     = SUM(ABS(Brand Expenses))
     Source QG Volume = SUM(ABS(Source Expenses where B = QG))  — PH excluded
     Net Volume       = Brand Volume - Source QG Volume         — may be negative */

const POLL_MS = 10000;

const StatusBadge = ({ status, testId }) =>
  status === "in_progress" ? (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700"
    >
      <CircleDot className="h-3 w-3" />
      Week in Progress
    </span>
  ) : (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-sky-700"
    >
      <CheckCircle2 className="h-3 w-3" />
      Week Closed
    </span>
  );

const VolumeStat = ({ label, value, testId }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span data-testid={testId} className="font-mono text-lg font-semibold tabular-nums text-foreground">
      {fmtCurrency(value)}
    </span>
  </div>
);

export const VolumeTracker = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async (withSpinner = false) => {
    if (withSpinner) setLoading(true);
    try {
      const res = await api.get("/volume-tracker");
      setData(res.data);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, []);

  // Fetch on open + live polling while the tracker stays open.
  useEffect(() => {
    if (!open) return undefined;
    load(true);
    const timer = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(timer);
  }, [open, load]);

  const cw = data?.current_week;
  const history = data?.history || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="volume-tracker-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Volume Tracker
          </DialogTitle>
          <DialogDescription>
            Read-only weekly net volume computed automatically from existing Expense data.
            Brand and Source QG expenses are converted to positive volumes; Source PH is excluded.
          </DialogDescription>
        </DialogHeader>

        {loading && !data ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error && !data ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">Failed to load volume data.</p>
            <Button variant="outline" size="sm" onClick={() => load(true)} data-testid="volume-retry-button">
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Retry
            </Button>
          </div>
        ) : cw ? (
          <div className="space-y-6">
            {/* CURRENT WEEK */}
            <section className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Current Week</h3>
                <StatusBadge status="in_progress" testId="volume-current-badge" />
              </div>
              <p data-testid="volume-current-period" className="mt-1 text-sm font-medium text-foreground">
                {cw.week_start ? formatPeriodRange(cw.week_start, cw.week_end) : "No active accounting period"}
              </p>

              <div className="mt-4 space-y-2.5">
                <VolumeStat label="Brand Volume" value={cw.brand_volume} testId="volume-current-brand" />
                <VolumeStat label="Source QG Volume" value={cw.source_qg_volume} testId="volume-current-qg" />
                <Separator className="my-3" />
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm font-bold uppercase tracking-wide text-foreground">Net Volume</span>
                  <span
                    data-testid="volume-current-net"
                    className={cn(
                      "font-mono text-2xl font-bold tabular-nums",
                      cw.net_volume < 0 ? "text-red-600" : "text-accent-foreground"
                    )}
                  >
                    {fmtCurrency(cw.net_volume)}
                  </span>
                </div>
              </div>
            </section>

            {/* WEEKLY HISTORY */}
            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Weekly History
              </h3>
              {history.length === 0 ? (
                <p
                  data-testid="volume-history-empty"
                  className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground"
                >
                  No closed weeks yet. Each week appears here, frozen, after the accounting week closes.
                </p>
              ) : (
                <div className="w-full overflow-x-auto rounded-md border border-border">
                  <Table data-testid="volume-history-table" className="min-w-[560px]">
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Week</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Brand Volume</TableHead>
                        <TableHead className="text-right">Source QG Volume</TableHead>
                        <TableHead className="text-right">Net Volume</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((h, idx) => (
                        <TableRow key={h.period_id} data-testid={`volume-history-row-${idx}`}>
                          <TableCell className="whitespace-nowrap text-sm font-medium">
                            {formatPeriodRange(h.week_start, h.week_end)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status="closed" testId={`volume-history-row-${idx}-badge`} />
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums">
                            {fmtCurrency(h.brand_volume)}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums">
                            {fmtCurrency(h.source_qg_volume)}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "text-right font-mono font-semibold tabular-nums",
                              h.net_volume < 0 ? "text-red-600" : "text-foreground"
                            )}
                          >
                            {fmtCurrency(h.net_volume)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>

            <p className="text-xs text-muted-foreground">
              Volume only — this tracker does not include payments, opening balances, or available balances.
              Values update automatically; nothing here can modify your accounting data.
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
